import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {TimezoneSource} from 'common/src/modules/timezones/timezones.types';
import timezonesService from 'common/src/modules/timezones/timezones.service';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import {ScheduleCardScratchpadState} from '../../scratchpad/scheduleCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const setTimeZoneInputSchema = z.object({
  timezoneQuery: z
    .string()
    .min(1)
    .describe(
      "Free-text timezone requested by the user (e.g. 'Israel', 'New York', 'UTC+2', 'Europe/London')",
    ),
});

const timezoneResponseSchema = z.object({
  timezoneIndex: z
    .number()
    .positive()
    .describe('The index number (1-based) of the timezone from the catalogue'),
  foundMatch: z.boolean().describe('Whether the timezone was found in the catalogue'),
});

const MAX_CANDIDATES = 50;

function buildCandidates(
  timezonesForSelector: Array<{value: string; label: string}>,
  query: string,
): Array<{id: string; label: string}> {
  const lower = query.toLowerCase().trim();
  const scored = timezonesForSelector.map((tz) => {
    const id = String(tz.value);
    const label = (tz.label ?? id).toLowerCase();
    let score = 0;
    if (id.toLowerCase().includes(lower)) score += 10;
    if (label.includes(lower)) score += 5;
    const words = lower.split(/\s+/);

    for (const w of words) {
      if (w.length > 1 && (id.toLowerCase().includes(w) || label.includes(w))) score += 2;
    }

    return {id, label: tz.label ?? id, score};
  });
  scored.sort((a, b) => b.score - a.score);
  const top = scored.filter((s) => s.score > 0).slice(0, MAX_CANDIDATES);

  if (top.length === 0) {
    return timezonesForSelector.slice(0, MAX_CANDIDATES).map((tz) => ({
      id: String(tz.value),
      label: tz.label ?? String(tz.value),
    }));
  }

  return top.map(({id, label}) => ({id, label}));
}

export default function setTimeZoneTool() {
  async function execute(
    params: ToolExecuteParams<typeof setTimeZoneInputSchema, ScheduleCardScratchpadState>,
  ) {
    const {timezoneQuery} = params.input;
    const {agentScratchpad, invokeStandaloneLLMCall} = params.toolUtils;
    const {callChain} = params;
    const validatedScratchpad = validateAgentScratchpad<ScheduleCardScratchpadState>(
      agentScratchpad,
      NodeType.SCHEDULE,
    );

    if (validatedScratchpad.card.timezoneSource !== TimezoneSource.Manual) {
      return toolResult(
        JSON.stringify({
          status: 'error',
          message:
            'Timezone can be set only when timezoneSource is Manual. Set timezone source to Manual first.',
        }),
      );
    }

    let dateTimeInfo;

    try {
      dateTimeInfo = await timezonesService.getDateTimeInfo();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolResult(
        JSON.stringify({status: 'error', message: `Failed to load timezones: ${message}`}),
      );
    }

    const timezonesForSelector = dateTimeInfo.timezonesForSelector ?? [];
    const timezoneMapper = dateTimeInfo.timezoneMapper ?? {};
    const candidates = buildCandidates(
      timezonesForSelector.map((tz) => ({value: tz.value, label: tz.label})),
      timezoneQuery,
    );

    if (candidates.length === 0) {
      return toolResult(
        JSON.stringify({status: 'error', message: 'No timezone options available.'}),
      );
    }

    const catalogue = candidates
      .map((c, index) => `${index + 1}. ${c.label} (ID: ${c.id})`)
      .join('\n');

    const systemPrompt = `You are a precise matcher that selects the single best timezone for a user's request.
You must return exactly one timezone from the numbered list below. Output only the index number (1-based) as timezoneIndex.

Strict rules:
- The timezoneIndex MUST be a number between 1 and ${candidates.length}.
- Do not invent numbers outside this range.
- Select the timezone that best matches the user's request.
- If the user's request is not found in the catalogue, return foundMatch as false and timezoneIndex as the UTC timezone index.

Timezone catalogue:
${catalogue}`;

    const messages = [
      openAISystemMessage(systemPrompt),
      openAIUserMessage(
        `User requested timezone: "${timezoneQuery}"\n\nReturn the index number of the best matching timezone from the catalogue.`,
      ),
    ];

    let result: z.infer<typeof timezoneResponseSchema>;

    try {
      result = await invokeStandaloneLLMCall({
        messages,
        responseSchema: timezoneResponseSchema,
        callChain,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolResult(
        JSON.stringify({status: 'error', message: `Failed to resolve timezone: ${message}`}),
      );
    }

    const timezoneIndex = result.timezoneIndex;

    if (!timezoneIndex || timezoneIndex < 1 || timezoneIndex > candidates.length) {
      return toolResult(
        JSON.stringify({
          status: 'error',
          message: `Invalid timezone index. Please specify a number between 1 and ${candidates.length}.`,
        }),
      );
    }

    const selectedCandidate = candidates[timezoneIndex - 1];
    const timezoneId = selectedCandidate.id;

    if (!timezoneMapper[timezoneId]) {
      return toolResult(
        JSON.stringify({
          status: 'error',
          message: `"${timezoneId}" is not a valid timezone. Please choose from the available timezones.`,
        }),
      );
    }

    validatedScratchpad.card.timeZone = timezoneId;

    const setTimezoneMessage = `Timezone set to ${selectedCandidate.label} (ID: ${timezoneId})`;

    if (!result.foundMatch) {
      return toolResult(
        JSON.stringify({
          status: 'success',
          message: `The requested timezone "${timezoneQuery}" was not found in the catalogue, timezone set to UTC.`,
        }),
      );
    }

    return toolResult(JSON.stringify({status: 'success', message: setTimezoneMessage}));
  }

  return tool<typeof setTimeZoneInputSchema, ScheduleCardScratchpadState>({
    name: V2CardAgentToolName.SetTimeZone,
    displayName: 'Setting timezone',
    description: 'Set the schedule timezone (Manual source only).',
    inputSchema: setTimeZoneInputSchema,
    execute,
  });
}
